import React from "react";

import { ApplicationBarNavigation } from "@impero-com/common/components/applicationBar/applicationBar";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { useParams } from "react-router-dom";

import { dashboardPendingActivitiesUrl } from "org/dashboardPendingActivities/urlUtils";
import { Guid } from "utils/guid";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { useNewMenu } from "../../../navigation/useNewMenu";
import {
    controlTestingActivityIdParamSchema,
    testingProgramListUrl,
} from "../../urlUtils";

import { useControlTestingCollectAnchors } from "./consts";
import { useControlActivitiesSamplesQuery } from "./queries/useControlActivitiesSamplesQuery";
import { useTestingActivitySuspenseQuery } from "./queries/useTestingActivityQuery";
import { TestingActivityView } from "./TestingActivityView";

export function TestingControlActivityViewPage() {
    const { id: unparsedActivityId } = useParams();
    const activityIdParseResult =
        controlTestingActivityIdParamSchema.safeParse(unparsedActivityId);
    if (activityIdParseResult.success === false) {
        console.error("Activity id must be a UUID, got:", unparsedActivityId);
        return <LoggedInHTTPError code={400} />;
    }

    const activityId: Guid = activityIdParseResult.data;

    return <PageView activityId={activityId} />;
}

interface PageViewProps {
    activityId: Guid;
}
function PageView({ activityId }: PageViewProps) {
    const { data: testingActivityData } =
        useTestingActivitySuspenseQuery(activityId);
    const { data: samplesData } = useControlActivitiesSamplesQuery(activityId);

    const { resource } = testingActivityData.content;

    const anchors = useControlTestingCollectAnchors(
        resource.activityKind,
        resource.currentStep
    );

    return (
        <TestingActivityAppBar
            testName={resource.title}
            navigation={{
                type: "Anchors" as const,
                anchors,
            }}
        >
            <TestingActivityView
                testingActivity={testingActivityData}
                activityId={activityId}
                controlActivitiesList={samplesData}
            />
        </TestingActivityAppBar>
    );
}

interface TestingActivityAppBarProps {
    testName: string;
    children: React.ReactNode;
    navigation?: ApplicationBarNavigation;
}
function TestingActivityAppBar({
    testName,
    children,
    navigation,
}: TestingActivityAppBarProps) {
    const { t } = useTranslation();
    const newMenu = useNewMenu();
    return (
        <OrgApplicationBar
            title={{
                breadCrumbs: [
                    {
                        label: t("ControlTesting_ModuleTitle"),
                        url: newMenu
                            ? dashboardPendingActivitiesUrl
                            : testingProgramListUrl,
                    },
                ],
                title: newMenu
                    ? testName
                    : t("ControlTestingActivityView_Title"),
            }}
            navigation={navigation}
        >
            {children}
        </OrgApplicationBar>
    );
}
